import { FloatingAlert } from "@ext/enterprise/components/admin/ui-kit/FloatingAlert";
import { SettingsSection } from "@ext/enterprise/components/admin/ui-kit/SettingsSection";
import { TableComponent } from "@ext/enterprise/components/admin/ui-kit/table/TableComponent";
import { TableToolbar } from "@ext/enterprise/components/admin/ui-kit/table/TableToolbar";
import { TableToolbarTextInput } from "@ext/enterprise/components/admin/ui-kit/table/TableToolbarTextInput";
import type { GesCloudApi, GesCloudMemberAiAgentAccess } from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { getCoreRowModel, getFilteredRowModel, useReactTable } from "@ui-kit/DataTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshDataButton } from "../../../components/RefreshDataButton";
import { AI_AGENT_ACCESS_EMAIL_COLUMN_ID, getAiAgentAccessTableColumns } from "./AiAgentAccessTableConfig";

interface AiAgentAccessTableProps {
	gesCloudApi: GesCloudApi;
	className?: string;
}

const withAiAgentAccess = (members: GesCloudMemberAiAgentAccess[], email: string, aiAgentAccess: boolean) =>
	members.map((member) => (member.email === email ? { ...member, aiAgentAccess } : member));

export const AiAgentAccessTable = ({ gesCloudApi, className }: AiAgentAccessTableProps) => {
	const [members, setMembers] = useState<GesCloudMemberAiAgentAccess[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const loadMembers = useCallback(async () => {
		try {
			setIsLoading(true);
			setMembers(await gesCloudApi.getMembersAiAgentAccess());
			setError(null);
		} catch {
			setError(t("enterprise-cloud.org-settings.ai-wallet.access.load-error"));
		} finally {
			setIsLoading(false);
		}
	}, [gesCloudApi]);

	useEffect(() => {
		void loadMembers();
	}, [loadMembers]);

	const setMemberAiAgentAccess = useCallback(
		async (email: string, aiAgentAccess: boolean) => {
			setMembers((current) => withAiAgentAccess(current, email, aiAgentAccess));
			try {
				await gesCloudApi.setMemberAiAgentAccess(email, aiAgentAccess);
				setError(null);
			} catch {
				setMembers((current) => withAiAgentAccess(current, email, !aiAgentAccess));
				setError(t("enterprise-cloud.org-settings.ai-wallet.access.save-error"));
			}
		},
		[gesCloudApi],
	);

	const columns = useMemo(() => getAiAgentAccessTableColumns(setMemberAiAgentAccess), [setMemberAiAgentAccess]);

	const table = useReactTable<GesCloudMemberAiAgentAccess>({
		data: members,
		columns,
		getRowId: (member) => member.email,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		defaultColumn: { size: 0 },
	});

	const emailColumn = table.getColumn(AI_AGENT_ACCESS_EMAIL_COLUMN_ID);

	return (
		<SettingsSection
			className={className}
			description={t("enterprise-cloud.org-settings.ai-wallet.access.description")}
			title={t("enterprise-cloud.org-settings.ai-wallet.access.title")}
		>
			<FloatingAlert message={error} show={Boolean(error)} />

			<TableToolbar
				className="pt-0"
				input={
					<TableToolbarTextInput
						onChange={(value) => emailColumn?.setFilterValue(value)}
						placeholder={t("enterprise-cloud.org-settings.users.placeholder")}
						value={(emailColumn?.getFilterValue() as string) ?? ""}
					/>
				}
			>
				<RefreshDataButton handleRefresh={loadMembers} />
			</TableToolbar>

			<TableComponent<GesCloudMemberAiAgentAccess> columns={columns} isLoading={isLoading} table={table} />
		</SettingsSection>
	);
};
